import { useState, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import useCompositionGuard from "../../hooks/useCompositionGuard";
import TeamPersonnelGroup from "../common/TeamPersonnelGroup";
import NapFieldsGroup, { napFieldsComplete, vacantPortsPayload } from "../common/NapFieldsGroup";
import ButtonSpinner from "../common/ButtonSpinner";
import FetchingOverlay from "../common/FetchingOverlay";
import { useConfirm } from "../../context/ConfirmContext";

/* ─── NI Cancellation reasons ─── */
const CANCELLATION_REASON_OPTIONS = [
  "Cancelled by Subs",
  "Needs RTA",
  "Full NAP",
  "No Converge Facility",
  "Other reason",
];

/* ─── Read-only record context shown at top of every quick-action dialog ─── */
function RecordInfo({ name, address, label, value }) {
  return (
    <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
      <p className="text-sm font-semibold text-gray-900 truncate" title={name}>{name || "—"}</p>
      <p className="text-xs text-gray-500">{address || "—"}</p>
      <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">{label}:</span> {value || "—"}</p>
    </div>
  );
}

/* ─── Self-contained Complete dialog for NI quick status action ─── */
export function QuickCompleteDialog({ jo, onDone, onCancel }) {
  const { user } = useAuth();
  const isTeamLocked = !!(jo.team_assigned_by_company && user?.company_id && jo.team_assigned_by_company !== user.company_id);
  const [teamNumber, setTeamNumber] = useState(jo.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(jo.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(jo.personnel_2_id || null);
  const [remark, setRemark] = useState(jo.jo_status_remarks || "");
  const [serialNumber, setSerialNumber] = useState(jo.serial_number || "");
  const [recordId, setRecordId] = useState(jo.record_id || "");
  const [napCode, setNapCode] = useState(jo.nap_code || "");
  const [napId, setNapId] = useState(jo.nap_id || "");
  const [napPort, setNapPort] = useState(jo.nap_port || "");
  const [vacantPorts, setVacantPorts] = useState(jo.vacant_ports ?? "");
  const [napPhoto, setNapPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { check: checkComp, ConflictDialog } = useCompositionGuard();
  const { confirm } = useConfirm();

  // Match team on mount
  useState(() => {
    if (jo.team_number) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === jo.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const canConfirm =
    remark.trim() && serialNumber.trim() && teamNumber.trim() && personnel1Id &&
    napFieldsComplete({ napCode, napId, napPort, vacantPorts }) && !!napPhoto;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: jo.job_date, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true);
    setError("");
    try {
      await api.put(`/job-orders/${jo.id}`, {
        jo_status_remarks: remark,
        serial_number: serialNumber,
        record_id: recordId || null,
        nap_code: napCode,
        nap_id: napId,
        nap_port: napPort,
        vacant_ports: vacantPortsPayload(vacantPorts),
        team_number: teamNumber,
        personnel_1_id: personnel1Id,
        personnel_2_id: personnel2Id,
      });
      const photoForm = new FormData();
      photoForm.append("file", napPhoto);
      await api.post(`/job-orders/${jo.id}/nap-photo`, photoForm);
      const { data } = await api.post(`/job-orders/${jo.id}/complete`);
      setSubmitting(false);
      await confirm({
        title: "Job Order Completed",
        message: data?.nap_details_updated
          ? "Job order completed successfully and NAP details updated."
          : "Job order completed successfully.",
        confirmLabel: "OK",
        cancelLabel: "OK",
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to complete job order.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <FetchingOverlay show={submitting} />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Complete Job Order</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to mark this job order as Completed?</p>
        <RecordInfo name={jo.customer_full_name} address={jo.complete_address} label="JO Number" value={jo.jo_number} />
        {isTeamLocked ? (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
            <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{teamNumber || "—"} <span title="Locked — assigned by another company">🔒</span></p>
          </div>
        ) : (
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={jo.job_date}
        />
        )}
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <NapFieldsGroup
          napCode={napCode} onNapCodeChange={setNapCode}
          napId={napId} onNapIdChange={setNapId}
          napPort={napPort} onNapPortChange={setNapPort}
          vacantPorts={vacantPorts} onVacantPortsChange={setVacantPorts}
          showPhoto napPhoto={napPhoto} onNapPhotoChange={setNapPhoto}
        />
        <label className="block text-sm font-medium mb-1">Modem Serial Number <span className="text-red-500">*</span></label>
        <input type="text" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="Enter Modem Serial Number..." className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">IPTV Serial Number</label>
        <input type="text" value={recordId} onChange={(e) => setRecordId(e.target.value)} placeholder="Enter IPTV Serial Number..." className="w-full border rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-green-600 hover:bg-green-700 inline-flex items-center gap-2">
            {submitting && <ButtonSpinner />}
            {submitting ? "Completing…" : "Confirm"}
          </button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

/* ─── Self-contained Cancel dialog for NI quick status action ─── */
export function QuickCancelDialog({ jo, onDone, onCancel }) {
  const { user } = useAuth();
  const isTeamLocked = !!(jo.team_assigned_by_company && user?.company_id && jo.team_assigned_by_company !== user.company_id);
  const [teamNumber, setTeamNumber] = useState(jo.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(jo.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(jo.personnel_2_id || null);
  const [remark, setRemark] = useState("");
  const [reasons, setReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (jo.team_number) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === jo.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const toggleReason = (reason) => {
    setReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    if (text) return text;
    return "";
  };

  const canConfirm = remark.trim() && reasons.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: jo.job_date, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/job-orders/${jo.id}/cancel`, {
        jo_status_remarks: buildFinalRemark(),
        cancellation_reasons: reasons,
        team_number: teamNumber || null,
        personnel_1_id: personnel1Id,
        personnel_2_id: personnel2Id,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to cancel job order.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Cancel Job Order</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to cancel this job order?</p>
        <RecordInfo name={jo.customer_full_name} address={jo.complete_address} label="JO Number" value={jo.jo_number} />
        {isTeamLocked ? (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
            <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{teamNumber || "—"} <span title="Locked — assigned by another company">🔒</span></p>
          </div>
        ) : (
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={jo.job_date}
        />
        )}
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
        <div className="space-y-1.5 mb-1">
          {CANCELLATION_REASON_OPTIONS.map((reason) => (
            <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={reasons.includes(reason)} onChange={() => toggleReason(reason)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              {reason}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">Will save as: <span className="font-medium">{buildFinalRemark()}</span></p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {reasons.length === 0 && !remark.trim()
              ? "Please select at least one cancellation reason and enter a remark."
              : reasons.length === 0
              ? "Please select at least one cancellation reason."
              : "Please enter a JO Status Remark."}
          </p>
        )}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-red-600 hover:bg-red-700">
            {submitting ? "Cancelling…" : "Confirm"}
          </button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}
